'use client'

import { Anchor } from "antd";
import { useGlobalContext } from "../../context/provider";

export default function CouponGuidePage() {
    const { screen, theme } = useGlobalContext();

    return (
        <div className={`flex flex-col md:flex-row gap-6 md:gap-8 pb-6 w-full min-w-0 font-sans ${theme || ''}`}>
            {/* Anchor Navigation Bar */}
            <div className="w-full md:w-[240px] lg:w-[260px] shrink-0 sticky top-2 md:top-4 z-10 page-surface p-3 md:p-4 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm md:shadow-none h-fit">
                <Anchor
                    offsetTop={100}
                    affix={false}
                    direction={screen === 'mobile' ? 'horizontal' : 'vertical'}
                    className="w-full overflow-x-auto [-webkit-overflow-scrolling:touch] [&_.ant-anchor-link-title]:!text-primary [&_.ant-anchor-link-title-active]:!text-[#EC008C] dark:[&_.ant-anchor-link-title-active]:!text-[#FF5C9F] [&_.ant-anchor-ink]:!bg-[#EC008C] dark:[&_.ant-anchor-ink]:!bg-[#FF5C9F]"
                    items={[
                        {
                            key: "1",
                            href: "#coupon-title",
                            title: <div className="text-primary text-xs sm:text-sm font-medium">🧾 วิธีการใช้งานคูปอง</div>,
                        },
                        {
                            key: "2",
                            href: "#discount-coupon",
                            title: <div className="text-primary text-xs sm:text-sm font-medium">🛍️ คูปองลดราคา</div>,
                        },
                        {
                            key: "3",
                            href: "#book-coupon",
                            title: <div className="text-primary text-xs sm:text-sm font-medium">📚 คูปองรับหนังสือ</div>,
                        },
                    ]}
                />
            </div>

            {/* Content */}
            <div className="flex-1 space-y-6 sm:space-y-8 min-w-0 px-2 sm:px-4 md:px-0 [overflow-wrap:anywhere] break-words">
                <div id="coupon-title" className="scroll-mt-24">
                    <h2 className="text-lg sm:text-xl font-bold mb-3 text-primary">
                        🧾 วิธีการใช้งานคูปอง (Coupon Usage Guide)
                    </h2>
                    <p className="text-sm sm:text-base text-primary mb-2">คูปองในระบบแบ่งออกเป็น 2 ประเภทหลัก:</p>
                    <ul className="list-disc text-primary pl-5 space-y-1 text-sm sm:text-base">
                        <li>🛍️ คูปองลดราคา (Discount Coupon)</li>
                        <li>📚 คูปองรับหนังสือ (Book Coupon)</li>
                    </ul>
                </div>

                {/* Discount Coupon Section */}
                <div id="discount-coupon" className="scroll-mt-24 space-y-3">
                    <h3 className="text-base sm:text-lg font-semibold text-[#EC008C] dark:text-[#FF5C9F]">
                        🛍️ คูปองลดราคา
                    </h3>
                    <p className="text-sm sm:text-base text-primary">
                        ใช้เพื่อลดราคาสินค้าในหน้าชำระเงิน โดยแบ่งเป็น 2 รูปแบบหลัก:
                    </p>

                    <div className="pl-2 sm:pl-4 space-y-3 border-l-2 border-[#EC008C]/30 dark:border-[#FF5C9F]/40 my-3">
                        <div>
                            <h4 className="font-bold text-sm sm:text-base text-primary">1. คูปองโค้ดลับ (Secret Code)</h4>
                            <ul className="list-disc text-primary pl-5 space-y-1 text-sm sm:text-base mt-1">
                                <li>ต้องกรอกโค้ดด้วยตัวเองในหน้าตะกร้า</li>
                                <li>ตัวพิมพ์เล็ก–ใหญ่ต้องตรง เช่น <code className="bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 px-2 py-0.5 rounded text-[#EC008C] dark:text-[#FF5C9F] font-mono text-xs sm:text-sm">caymmgSH</code></li>
                                <li>ไม่สามารถรับจากหน้าโปรโมชั่นได้โดยตรง</li>
                            </ul>
                        </div>

                        <div>
                            <h4 className="font-bold text-sm sm:text-base text-primary">2. คูปองกดรับ (Event Coupon)</h4>
                            <ul className="list-disc text-primary pl-5 space-y-1 text-sm sm:text-base mt-1">
                                <li>กดรับได้จากหน้า “โปรโมชั่น”</li>
                                <li>ระบบจะเก็บไว้ใน “คูปองของฉัน”</li>
                                <li>เลือกใช้ได้ในหน้าตะกร้า โดยกด “เลือกคูปอง”</li>
                            </ul>
                        </div>
                    </div>

                    <p className="text-xs sm:text-sm font-bold text-amber-700 dark:text-amber-500 bg-amber-50 dark:bg-amber-950/90 p-3 rounded-lg border border-amber-200 dark:border-amber-800/50">
                        ⚠️ หมายเหตุ: คูปองหมดอายุจะไม่สามารถใช้ได้ และใช้ได้เพียง 1 ใบต่อคำสั่งซื้อ
                    </p>
                </div>

                {/* Book Coupon Section */}
                <div id="book-coupon" className="scroll-mt-24 space-y-3">
                    <h3 className="text-base sm:text-lg font-semibold text-[#EC008C] dark:text-[#FF5C9F]">
                        📚 คูปองรับหนังสือ
                    </h3>
                    <p className="text-sm sm:text-base text-primary">คูปองประเภทนี้ใช้เพื่อ “รับนิยายหรือหนังสือฟรี” มี 2 แบบคือ:</p>

                    <ul className="list-disc text-primary pl-5 space-y-2 text-sm sm:text-base">
                        <li>
                            <b>อ่านชั่วคราว:</b> หนังสือจะเข้าในคลังชั่วคราว และหมดอายุในระยะเวลาที่กำหนด (เช่น 3 หรือ 7 วัน)
                        </li>
                        <li>
                            <b>ได้รับถาวร:</b> หนังสือจะถูกเพิ่มในคลังถาวรของผู้ใช้ สามารถอ่านได้ตลอดไม่มีวันหมดอายุ
                        </li>
                    </ul>

                    <div className="p-3 sm:p-4 rounded-xl bg-pink-50/70 dark:bg-pink-950/30 border border-pink-100 dark:border-pink-900/40 text-xs sm:text-sm text-primary">
                        🔸 <b>วิธีใช้:</b> ไปที่เมนู “กรอกคูปอง” → กรอกโค้ดให้ตรงทุกตัวอักษร (พิมพ์เล็ก–ใหญ่ต้องตรง) เมื่อสำเร็จ ระบบจะเพิ่มหนังสือให้ในคลังอัตโนมัติ
                    </div>
                </div>
            </div>
        </div>
    );
}
